/**
 * PQR field-link registry.
 *
 * Single typed source of truth mapping `(pqrSection, fieldKey, …)` to a
 * concrete navigation target on the WPS Studio source page. Imported by
 * `PqrPreviewFieldLink` to build URLs from a click, and by each
 * destination page's `useFieldFocusHandler` registry to interpret the
 * focus params on arrival.
 *
 * Slice 01 ships the `notes-welder` variant only — every other variant
 * (notes-equipment, materials, joint-prep, welding-variables,
 * welding-parameters) is added in subsequent slices by extending the
 * `PqrFieldDescriptor` union and adding one `case` to `buildPqrFieldNavTarget`.
 *
 * Aggregated mode (no scoped weld) → `null` target → caller renders the
 * link disabled with a tooltip. See PRD: aggregated mode is the only
 * case where clicks are intentionally inert.
 */

export type PqrFieldDescriptor =
  | { section: 'notes-welder'; equipmentKey: string }
  /**
   * Slice 03 of pqr-preview-field-navigation. The Comments cell on the
   * canvas mirrors the picked weld's FloorSheet `operators.notes`
   * textarea; clicking it navigates to that textarea so the user can edit.
   */
  | { section: 'comments-notes' }
  /**
   * Multi-weld PQR (multi-weld-pqr). The Comments cell is now sourced
   * from the WpsForm's global `sections.comments.comments` field rather
   * than per-weld FloorSheet notes; clicking it navigates back to the
   * WpsForm page with `focusField=notesTextarea` so the shared textarea
   * is scrolled/focused (same marker as WPS Preview Notes).
   */
  | { section: 'comments-wpsform' }
  /**
   * Slice 04 of pqr-preview-field-navigation. Notes equipment rows on
   * the canvas mirror the project's `equipment[]` list; clicking them
   * navigates to the project edit page's equipment MultiSelect.
   */
  | { section: 'notes-equipment' }
  /**
   * Slice 05 of pqr-preview-field-navigation. Per-pipe row on the
   * Materials table; navigates to the WpsBaseMaterial page with the
   * specified pipe row scrolled into view + flashed + focused.
   */
  | { section: 'materials'; pipeIndex: number }
  /**
   * Slice 06 of pqr-preview-field-navigation. Joint Preparation panel
   * on the canvas; navigates to the WpsBevelDetails page with the
   * specified bevel tab activated + flashed + focused. Pipe-set-wide so
   * no weld scope is required.
   */
  | { section: 'joint-preparation'; bevelIndex: number }
  /**
   * Slice 07 of pqr-preview-field-navigation. Per-cell on the Welding
   * Variables matrix; navigates to WpsWeldDetails for the chosen
   * `passLabel` with the specified field highlighted. Pipe-set-wide so
   * no weld scope is required.
   */
  | {
      section: 'welding-variable-cell'
      bevelIndex: number
      passLabel: string
      rowKey: string
    }
  /**
   * Slice 08 of pqr-preview-field-navigation. Per-cell on the Welding
   * Parameters matrix; navigates to the active weld's WeldParameters
   * page so the user can inspect/correct the telemetry feeding the
   * cell. Weld-scoped because telemetry lives per weld.
   */
  | {
      section: 'welding-parameter-cell'
      passLabel: string
      columnKey: string
    }
  /**
   * Slice 09 of pqr-preview-field-navigation. Equipment-class notes
   * rows on the canvas split into three families so each clicks
   * through to the WPS source page that owns the data:
   *
   *  - `equipment-clamp`     -> WpsClampDetails  (/clamp)
   *  - `equipment-preheat`   -> WpsPreheatDetails (/preheat)
   *  - `equipment-post-weld` -> WpsPostWeld       (/post-weld)
   *
   * `fieldKey` is the `data-focus-field` marker on the destination
   * page; left as `null` when no specific marker is desired (just open
   * the page at the top). All three are pipe-set-wide so they remain
   * navigable in aggregated mode.
   */
  | {
      section: 'equipment-clamp'
      fieldKey: string | null
    }
  | {
      section: 'equipment-preheat'
      fieldKey: string | null
    }
  | {
      section: 'equipment-post-weld'
      fieldKey: string | null
    }
  | {
      section: 'equipment-floorsheet-times'
      fieldKey: string
    }
  /**
   * Floor Sheet preview canvas sections (Root Bead, Target Values,
   * Preheat, Deposition, Bevel Measurements). Whole-section click
   * navigates to Floor Sheet with the matching CRC tab selected via
   * `focusPass`. Weld-scoped — aggregated mode disables the link.
   */
  | {
      section: 'floorsheet-section'
      tabKey: string
      /** Optional override when the sheet's weld differs from context.weldId. */
      weldId?: string
    }
  /**
   * Opens Floor Sheet Measurement Settings on a specific tab (e.g. Target
   * Values). Weld-scoped; uses `focusField=settings.<tab>`.
   */
  | {
      section: 'floorsheet-settings'
      settingsTab: 'targets'
      weldId?: string
    }

export interface PqrFieldLinkContext {
  projectId: string
  pipeSetId: string
  /**
   * User-typed `weldId` (matches `floorsheet.welds[i].weldId`) for the
   * currently scoped weld; `null` in aggregated mode. This is what the
   * server's `resolvePqrWeldScope` looks up and what the PQR Preview
   * `?weldId=` query carries. For multi-weld PQRs this is the FIRST
   * picked weld (the "primary" / back-compat scope).
   */
  weldId: string | null
  /**
   * Full multi-weld scope (added in `multi-weld-pqr`). Empty array =
   * legacy single-weld mode (`weldId` carries the only scope) OR
   * aggregated mode. When the user picked >1 weld on the picker this
   * holds every picked id in pick order, and the per-link `weldId`
   * override on `PqrFieldDescriptor` consumers selects which weld a
   * stacked row routes to.
   */
  weldIds?: string[]
  /**
   * Synthetic internal `id` (matches `floorsheet.welds[i].id`,
   * e.g. `weld-1780408430439-1-b7b6`) for the same scoped weld.
   * `null` when the user-typed `weldId` can't be resolved to a stored
   * weld OR in aggregated mode. Used by destinations whose URLs route
   * by the synthetic id \u2014 currently `/floorsheet/:weldId/parameters`,
   * whose `:weldId` route param is misnamed and actually expects
   * `w.id`. Keep this populated from `PqrPreviewPage` by looking up
   * `welds.find(w => w.weldId === activeWeldId)?.id`.
   */
  syntheticWeldId: string | null
  /** Pathname + search of the PQR preview page; round-tripped as `returnTo`. */
  previewUrl: string
}

export interface PqrFieldNavTarget {
  /** Destination pathname only. */
  destinationPath: string
  /** Focus + return params destined for the URL. */
  focusParams: Record<string, string>
  /** Decoded preview URL — same value the destination page reads back as `returnTo`. */
  returnTo: string
  /** Convenience: `destinationPath?` + URL-encoded params. Safe to pass to `navigate`. */
  fullUrl: string
  /** Human label for the destination, used in the confirm modal. */
  destinationLabel: string
}

function buildSearch(focusParams: Record<string, string>, returnTo: string): string {
  const params = new URLSearchParams()
  Object.entries(focusParams).forEach(([k, v]) => params.set(k, v))
  params.set('returnTo', returnTo)
  return params.toString()
}

/**
 * Inverse of `labelFromOperatorFieldKey` in
 * `server/src/pdf/pqrDataMapper.ts`. Given a humanised welder role
 * shown on the PQR canvas (e.g. "IWM Operator", "P-600Z CW Welder"),
 * recover the `operatorMap` key used by FloorSheet's storage and the
 * focus-field URL contract.
 *
 * Returns `null` when the role doesn't match any known pattern — in
 * which case the welder row renders without a navigation link (the
 * legacy `welder` section pre-FloorSheet has no per-key destination).
 *
 * Keep this function in lockstep with the server's
 * `labelFromOperatorFieldKey` and FloorSheet's `getOperatorFields`. A
 * change in any one of the three demands a matching change in the
 * other two.
 */
export function deriveEquipmentKeyFromWelderRole(role: string): string | null {
  if (!role) return null
  if (role === 'IWM Operator') return 'IWM'
  if (role === 'Manual Welding Operator') return 'Manual Welding'
  if (role === 'Root Misfire Welder') return 'Semi-Automatic Welding'
  if (role === 'Recorded By') return 'Recorded'
  // PDF/Notes display normalizes P-600Z → P600; map back to the Floor Sheet
  // operatorMap key used by CRC projects.
  if (role === 'P600 CW Welder') return 'P-600Z CW'
  if (role === 'P600 CCW Welder') return 'P-600Z CCW'
  const m = role.match(/^(.+) (CW|CCW) Welder$/)
  if (m) return `${m[1]} ${m[2]}`
  return null
}

/**
 * Stable mapping from server-side equipment-row labels (emitted by
 * `server/src/pdf/pqrDataMapper.ts`) to the source WPS page +
 * `data-focus-field` marker that owns the data. Returns `null` for
 * labels not in this list \u2014 the caller should fall back to the
 * generic `notes-equipment` descriptor (project edit page).
 *
 * Keep in lockstep with the labels pushed in `pqrDataMapper.ts`:
 *   - 'Welding Power Supplies' / 'Fronius Firmware/Database Ver.' /
 *     'Fronius Program' / 'Clamp Type' / 'Clamp Release' \u2192 WpsClampDetails
 *   - 'Preheat Method'                            \u2192 WpsPreheatDetails
 *   - 'Cleaning, Grinding'                        \u2192 WpsPostWeld
 *   - 'Root Complete' / 'Hot Pass Start' / 'Weld Completed'
 *                                                 \u2192 Floor Sheet times
 */
export function classifyEquipmentNoteLabel(
  label: string
): PqrFieldDescriptor | null {
  const key = label.trim()
  switch (key) {
    case 'Welding Power Supplies':
      return { section: 'equipment-clamp', fieldKey: 'weldingPowerSupplies' }
    case 'Firmware / Database Version':
    case 'Fronius Firmware/Database Ver.':
      return { section: 'equipment-clamp', fieldKey: 'firmware' }
    case 'Program':
    case 'Fronius Program':
      return { section: 'equipment-clamp', fieldKey: 'program' }
    case 'Clamp Type':
      return { section: 'equipment-clamp', fieldKey: 'clampType' }
    case 'Clamp Release':
      return { section: 'equipment-clamp', fieldKey: 'clampRelease' }
    case 'Preheat Method':
      return { section: 'equipment-preheat', fieldKey: 'method' }
    case 'Cleaning / Grinding':
    case 'Cleaning, Grinding':
      return { section: 'equipment-post-weld', fieldKey: 'cleaningNotes' }
    case 'Root External Treatment':
      return { section: 'equipment-post-weld', fieldKey: 'rootExternalTreatment' }
    case 'Root Complete':
    case 'Hot Pass Start':
    case 'Weld Completed':
      return { section: 'equipment-floorsheet-times', fieldKey: key }
    default:
      return null
  }
}

export function buildPqrFieldNavTarget(
  descriptor: PqrFieldDescriptor,
  context: PqrFieldLinkContext
): PqrFieldNavTarget | null {
  // Most variants require a scoped weld so the destination page can land
  // on the right per-weld row. Exceptions navigate to pipe-set-wide /
  // project-wide pages where no weld context is needed.
  // floorsheet-section may carry its own weldId (per Floor Sheet canvas).
  const noWeldRequired = [
    'notes-equipment',
    'materials',
    'joint-preparation',
    'welding-variable-cell',
    'equipment-clamp',
    'equipment-preheat',
    'equipment-post-weld',
    'comments-wpsform'
  ] as const
  const hasDescriptorWeld =
    (descriptor.section === 'floorsheet-section' ||
      descriptor.section === 'floorsheet-settings') &&
    Boolean(descriptor.weldId)
  if (
    !context.weldId &&
    !hasDescriptorWeld &&
    !(noWeldRequired as readonly string[]).includes(descriptor.section)
  ) {
    return null
  }

  switch (descriptor.section) {
    case 'notes-welder': {
      // The pipe-set-wide guard above leaves weldId possibly null in TS's
      // eyes (it can't narrow through `Array.includes`), but this branch
      // is per-weld so we re-check explicitly.
      if (!context.weldId) return null
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/floorsheet`
      const focusParams: Record<string, string> = {
        focusWeldId: context.weldId,
        focusPass: 'Operator Information',
        focusField: `operatorMap.${descriptor.equipmentKey}`
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Floor Sheet'
      }
    }
    case 'comments-notes': {
      if (!context.weldId) return null
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/floorsheet`
      const focusParams: Record<string, string> = {
        focusWeldId: context.weldId,
        focusPass: 'Notes',
        focusField: 'operators.notes'
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Floor Sheet — Notes'
      }
    }
    case 'comments-wpsform': {
      // The WpsForm page (`/projects/:id/next/:pipeId` index route) owns
      // the global `sections.comments.comments` field that the PQR
      // canvas Comments cell now mirrors.
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}`
      const focusParams: Record<string, string> = { focusField: 'notesTextarea' }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'WPS — Comments'
      }
    }
    case 'notes-equipment': {
      // Equipment is a project-wide concept; no weld scope required.
      const destinationPath = `/projects/${context.projectId}/edit`
      const focusParams: Record<string, string> = { focusField: 'equipment' }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Project — Equipment'
      }
    }
    case 'materials': {
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/base`
      const focusParams: Record<string, string> = {
        focusPipe: String(descriptor.pipeIndex)
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Base Material'
      }
    }
    case 'joint-preparation': {
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/bevel`
      const focusParams: Record<string, string> = {
        focusBevel: String(descriptor.bevelIndex)
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Bevel Details'
      }
    }
    case 'welding-variable-cell': {
      // WpsWeldDetails owns a separate URL per pass; URL-encode the
      // pass label because labels like "Fill 1" contain a space.
      const destinationPath =
        `/projects/${context.projectId}/next/${context.pipeSetId}/pass/bevel/` +
        `${descriptor.bevelIndex}/${encodeURIComponent(descriptor.passLabel)}`
      const focusParams: Record<string, string> = { focusField: descriptor.rowKey }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: `Weld Details — ${descriptor.passLabel}`
      }
    }
    case 'equipment-clamp': {
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/clamp`
      const focusParams: Record<string, string> = {}
      if (descriptor.fieldKey) focusParams.focusField = descriptor.fieldKey
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Clamp Details'
      }
    }
    case 'equipment-preheat': {
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/preheat`
      const focusParams: Record<string, string> = {}
      if (descriptor.fieldKey) focusParams.focusField = descriptor.fieldKey
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Preheat Details'
      }
    }
    case 'equipment-post-weld': {
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/post-weld`
      const focusParams: Record<string, string> = {}
      if (descriptor.fieldKey) focusParams.focusField = descriptor.fieldKey
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Post Weld'
      }
    }
    case 'equipment-floorsheet-times': {
      if (!context.weldId) return null
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/floorsheet`
      const fieldKey =
        descriptor.fieldKey === 'Root Complete'
          ? 'fsTimes.rootComplete'
          : descriptor.fieldKey === 'Hot Pass Start'
            ? 'fsTimes.hotPassStart'
            : 'fsTimes.weldCompleted'
      const focusParams: Record<string, string> = {
        focusWeldId: context.weldId,
        focusPass: 'Weld Completion Times',
        focusField: fieldKey
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Floor Sheet — Times'
      }
    }
    case 'floorsheet-section': {
      const weldId = descriptor.weldId ?? context.weldId
      if (!weldId) return null
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/floorsheet`
      const focusParams: Record<string, string> = {
        focusWeldId: weldId,
        focusPass: descriptor.tabKey,
        focusField: `floorsheetTab.${descriptor.tabKey}`
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: `Floor Sheet — ${descriptor.tabKey}`
      }
    }
    case 'floorsheet-settings': {
      const weldId = descriptor.weldId ?? context.weldId
      if (!weldId) return null
      const destinationPath = `/projects/${context.projectId}/next/${context.pipeSetId}/floorsheet`
      const focusParams: Record<string, string> = {
        focusWeldId: weldId,
        focusField: `settings.${descriptor.settingsTab}`
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: 'Floor Sheet — Target Values'
      }
    }
    case 'welding-parameter-cell': {
      // WeldParameters is per-weld telemetry \u2014 needs a weld scope.
      // CRITICAL: the route `/floorsheet/:weldId/parameters` is misnamed:
      // its `:weldId` param actually contains the synthetic
      // `floorsheet.welds[i].id` (e.g. `weld-1780408430439-1-b7b6`),
      // NOT the user-typed `weldId` that PQR Preview's `?weldId=` query
      // carries. We route with `syntheticWeldId` and bail when it's
      // unavailable (otherwise WeldParameters would render an empty
      // page because `welds.find(w => w.id === <user-typed>)` misses).
      if (!context.syntheticWeldId) return null
      const destinationPath =
        `/projects/${context.projectId}/next/${context.pipeSetId}/floorsheet/` +
        `${context.syntheticWeldId}/parameters`
      const focusParams: Record<string, string> = {
        focusField: descriptor.columnKey,
        focusPass: descriptor.passLabel
      }
      const search = buildSearch(focusParams, context.previewUrl)
      return {
        destinationPath,
        focusParams,
        returnTo: context.previewUrl,
        fullUrl: `${destinationPath}?${search}`,
        destinationLabel: `Weld Parameters — ${descriptor.passLabel}`
      }
    }
  }
}
